// Package imports:
import { useEffect, useState } from 'react';

/**
 * Custom hook for scroll spy functionality with support for fixed header offset
 * @param initialSection - The ID of the section that should be active on mount
 * @param parentClass - The class name of the parent container holding all sections
 * @param scrollOffset - Additional pixel offset to add on top of the header height (default: 0)
 */
const UseScrollSpy = (initialSection: string, parentClass: string, scrollOffset: number = 0) => {
    // CSS selector for the fixed header element to calculate its height
    const offsetElement = '.KCL_header';

    // Track which section is currently active/visible in the viewport
    const [currentSection, setCurrentSection] = useState(initialSection);

    /**
     * Calculate the total offset needed for scroll calculations
     * This includes the fixed header height plus any additional offset passed via props
     * @returns The total offset in pixels
     */
    const getTotalOffset = () => {
        const headerElement = document.querySelector(offsetElement);
        const headerHeight = headerElement?.clientHeight ?? 0;
        // Add the scrollOffset prop to the header height and a 30px buffer
        return headerHeight + scrollOffset + 30;
    };

    /**
     * Smoothly scroll to a specific section by its ID
     * Accounts for the fixed header height and additional offset
     * @param id - The ID of the section to scroll to
     */
    const scrollToSection = ((id: string) => {
        // Only scroll if we're not already at this section
        if (currentSection !== id) {
            const scrollToElement = document.getElementById(id);
            if (scrollToElement === null) return;

            // Calculate the scroll position: element position minus total offset
            const elementScrollDistance = scrollToElement.offsetTop - getTotalOffset();

            // Perform smooth scroll to the calculated position
            window.scrollTo({
                top: elementScrollDistance,
                behavior: 'smooth'
            });
        }
        // Update the current active section
        setCurrentSection(id);
    });

    /**
     * Determine which section is currently visible in the viewport
     * This runs on scroll events to update the active navigation item
     */
    const handleScrolling = () => {
        // Find all sections that should be tracked by scroll spy
        const scrollElements = document.querySelectorAll(`.${parentClass} .scrollspy-section`);
        const totalOffset = getTotalOffset();

        // Check each section to see if it's currently in the viewport
        scrollElements.forEach(el => {
            const elementTop = el.getBoundingClientRect().top;
            const elementBottom = el.getBoundingClientRect().bottom;

            // A section is "active" if its top edge is above the offset line
            // and its bottom edge is below the offset line
            if (elementTop <= totalOffset && elementBottom >= totalOffset) {
                const id = el.id;
                setCurrentSection(id);
            }
        });
    };

    // Set up scroll event listener on component mount
    useEffect(() => {
        window.addEventListener('scroll', handleScrolling);

        // Clean up the event listener when component unmounts
        return () => {
            window.removeEventListener('scroll', handleScrolling);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        currentSection,
        scrollToSection
    };
};

export default UseScrollSpy;